import { useContext, useState } from "react";

import { Button, CircularProgress } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import toast from "react-hot-toast";

import { useAddTrainingMeeting } from "../../../store";
import { Meeting, MeetingStatus } from "../../../types";
import { getMeetingStatus } from "../../../types-helpers";
import { AuthContext } from "../../../context/auth-context";
import { createMeetingURL } from "../../../utils/create-meeting-url";

interface TrainingMeetingData {
  meetingId: string,
  meetingType: MeetingStatus,
  startTime: Date,
  endTime: Date,
}

const trainingMeetingKey = "TrainingMeeting"

const TrainingMeetingButton: React.FC = () => {

  const baseUrl = window.location.origin

  const { userId } = useContext(AuthContext);

  const getTrainingMeeting = (): TrainingMeetingData | null => {
    const meetingString = localStorage.getItem(trainingMeetingKey);

    if (!meetingString) {
      return null
    }

    let meeting: TrainingMeetingData | null = null

    try {
      const parsedItem = JSON.parse(meetingString);
      meeting = {
        meetingId: parsedItem.meetingId,
        meetingType: MeetingStatus.Training,
        startTime: new Date(parsedItem.startTime),
        endTime: new Date(parsedItem.endTime),
      };

      return meeting

    } catch (e: any) {
      console.warn("Error parsing training meeting id", e);
      return null
    }
  }

  const [trainingMeeting, setTrainingMeeting] = useState<TrainingMeetingData | null>(getTrainingMeeting());
  const [creationInProgress, setCreationInProgress] = useState(false);

  const meetingAddSuccess = (meeting: Meeting | null) => {
    if (!meeting) {
      meetingAddError("Null meeting")
      return
    }

    const trainingMeeting: TrainingMeetingData = {
      meetingId: meeting.meetingId,
      meetingType: MeetingStatus.Training,
      startTime: meeting.startTime,
      endTime: meeting.endTime,
    }

    setTrainingMeeting(trainingMeeting);
    setCreationInProgress(false);

    localStorage.setItem(trainingMeetingKey, JSON.stringify(trainingMeeting));

    joinAndOpenMeeting(meeting?.meetingId);
  };

  const joinAndOpenMeeting = (meetingId?: string) => {
    if (!meetingId) {
      console.warn("No meeting id to join");
      return;
    }

    const url = createMeetingURL(
      baseUrl,
      meetingId,
      userId,
      undefined,
      "training"
    );

    window.open(url, "_blank");
  };

  const meetingAddError = (error: unknown) => {
    toast.error("Could not create training meeting");
    console.error("Failed to create training meeting", error)

    setCreationInProgress(false);
  };

  const { mutate: addTrainingMeeting } = useAddTrainingMeeting(
    meetingAddSuccess,
    meetingAddError
  );

  const createTrainingMeeting = () => {
    if (creationInProgress) {
      return;
    }

    if (!trainingMeeting) {
      addTrainingMeeting()
      setCreationInProgress(true)
      return
    }

    const { canJoin } = getMeetingStatus(trainingMeeting)

    if (canJoin) {
      joinAndOpenMeeting(trainingMeeting?.meetingId)
    } else {
      addTrainingMeeting()
      setCreationInProgress(true)
    }
  };

  return (
    <>
      {creationInProgress ? (
        <Button
          variant="contained"
          disabled
          sx={{
            borderRadius: "1.5rem",
            px: 2,
            py: 1,
            height: "40px",
            fontWeight: 600,
            border: "1px solid #ccc",
          }}
        >
          <CircularProgress size={16} sx={{ mr: 1 }} />
          Creating Meeting
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          startIcon={<SchoolIcon />}
          onClick={createTrainingMeeting}
          disabled={creationInProgress}
          sx={{
            borderRadius: "1.5rem",
            px: 2,
            py: 1,
            height: "40px",
            fontWeight: 600,
          }}
        >
          {creationInProgress ?
            "Creating Training Meeting..." :
            "Start Training Meeting"}
        </Button>
      )}
    </>
  );
};

export default TrainingMeetingButton;
